import { FileTextOutlined } from '@ant-design/icons';
import {
  EnumTipoDomandaModuloDTOTipoAssociazione,
  ModuloSezioneDTO,
  RichiestaModuloDTO,
} from '@framework/api-client';
import { t } from '@framework/i18n';
import React from 'react';
import {
  ISubmissionMenuItem,
  SubmissionMenuItemType,
} from '../menu/ISubmissionMenuItem';
import { mapSubmissionModuleState } from '../state/mapSubmissionModuleState';
import { buildModuleMenuItems } from './getSecondaryModuleMenuItems';
import { IGetSubmissionMenuLink } from './getSubmissionMenuItemLink';

export function getSectionKey(section: ModuloSezioneDTO | null | undefined) {
  return section?.sezione?.id ?? 'undefined';
}

export function getModuleKey(module: RichiestaModuloDTO | null | undefined) {
  return `${module?.id ?? 'undefined'}`;
}

export function getSectionTitle(
  section: ModuloSezioneDTO | null | undefined,
  i: number,
) {
  return (
    section?.sezione?.ultimaVersione?.titoloAbbreviato ??
    section?.sezione?.ultimaVersione?.nome ??
    t('submission-menu-items.sezione', `Sezione {{index}}`, {
      index: i + 1,
    })
  );
}

function getSectionMenuItem(input: {
  section: ModuloSezioneDTO;
  index: number;
  module: RichiestaModuloDTO;
  getMenuLink: IGetSubmissionMenuLink;
  submissionId: string;
  isSent: boolean;
}) {
  const { section, index, module, getMenuLink, submissionId, isSent } = input;
  return {
    key: getSectionKey(section),
    index: getSectionKey(section),
    name: getSectionTitle(section, index),
    type: SubmissionMenuItemType.Section,
    to: getMenuLink({
      submissionId,
      type: 'section',
      module: module,
      sectionId: section.sezione?.id,
      options: { isSent },
    }),
    visible: section.regoleAbilitazione?.visible,
    enabled: section.regoleAbilitazione?.enabled,
    context: {
      submissionModuleId: module.id ?? '<undefined>',
      sectionId: section.sezione?.id ?? '<undefined>',
    },
  };
}

export function getSectionsMenuItems(input: {
  sections: ModuloSezioneDTO[] | null | undefined;
  module: RichiestaModuloDTO;
  getMenuLink: IGetSubmissionMenuLink;
  submissionId: string;
  isSent: boolean;
  /**
   * Permette, nel caso di un solo modulo, di nascondere il sottomenu sezioni se è presente un'unica sezione.
   * Da specifiche:
   * se è presente solo un modulo (che è sempre il caso della regione) con una sola sezione, allora si nasconde la singola sezione e quindi vedo solo il modulo;
   * se invece sono presente “n” moduli di cui uno solo con una sola sezione, in questo caso si mostrerà sia modulo + sezione.
   */
  hideSectionIfSingle: boolean;
}) {
  const {
    sections,
    module,
    getMenuLink,
    submissionId,
    isSent,
    hideSectionIfSingle,
  } = input;
  if (!sections) return undefined;

  // Se richiesto, nel caso in cui ci sia una o meno sezioni, torna array vuoto.
  // In questo modo non mostriamo le sezioni per i moduli mono-sezione, come richiesto (28/02/2024)
  if (hideSectionIfSingle && sections.length <= 1) return undefined;
  return sections.map((s, i) =>
    getSectionMenuItem({
      section: s,
      index: i,
      module,
      getMenuLink,
      submissionId,
      isSent,
    }),
  );
}

interface IParams {
  modules: RichiestaModuloDTO[] | undefined | null;
  getMenuLink: IGetSubmissionMenuLink;
  submissionId: string;
  isSent: boolean;
}

/**
 * Funzione che ritorna i menu items di una lista di moduli
 * di una richiesta.
 */
export function getSubmissionModuleMenuItems(params: IParams) {
  const { modules, getMenuLink, submissionId, isSent } = params;

  if (!modules) return [];

  const principal = modules.find(
    m =>
      m.domandaModulo?.tipoAssociazione ===
      EnumTipoDomandaModuloDTOTipoAssociazione.PRINCIPALE,
  );
  const secondary = modules.filter(
    m =>
      m.domandaModulo?.tipoAssociazione ===
      EnumTipoDomandaModuloDTOTipoAssociazione.SECONDARIO,
  );

  const triggered = modules.filter(
    m =>
      m.domandaModulo?.tipoAssociazione ===
      EnumTipoDomandaModuloDTOTipoAssociazione.DINAMICO,
  );

  const modulesItems: ISubmissionMenuItem[] = [];

  if (principal?.id && principal?.modulo?.modulo) {
    const principalModule = principal.modulo.modulo;

    const principalChildren = {
      key: getModuleKey(principal),
      index: getModuleKey(principal),
      name:
        principalModule.titolo ??
        t('submission-menu-items.modulo-principale', 'Modulo Principale'),
      type: SubmissionMenuItemType.Principal,
      icon: <FileTextOutlined aria-hidden />,
      context: {
        submissionModuleId: principal.id,
        validationState: principal.statoValidazione,
        state: mapSubmissionModuleState(principal.statoCompilazione),
      },
      children: getSectionsMenuItems({
        sections: principal.modulo.sezioni,
        module: principal,
        getMenuLink,
        submissionId,
        isSent,
        // Se non ci sono moduli secondari, nascondi le sezioni se è presente un'unica sezione
        hideSectionIfSingle: secondary.length === 0,
      }),
      to: getMenuLink({
        submissionId,
        type: 'module',
        module: principal,
        options: { isSent },
      }),
    };
    modulesItems.push(principalChildren);
  }

  if (secondary.length > 0) {
    const secondaryChildren = secondary.map(module => {
      return buildModuleMenuItems({ module, ...params });
    });
    modulesItems.push(...secondaryChildren);
  }

  if (triggered.length > 0) {
    const triggeredChildren = triggered.map(module => {
      return buildModuleMenuItems({ module, ...params });
    });
    modulesItems.push(...triggeredChildren);
  }

  return modulesItems;
}
